import { createFileRoute } from '@tanstack/react-router';
import * as React from 'react';

import { PageHeader } from '@/components/dashboard/page-header';
import { trpc } from '@/lib/trpc/client';

export const Route = createFileRoute('/_admin/admin/delays')({
  component: DelaysPage,
});

const MAX_HOURS = 720;

interface Draft {
  bidMigrationHours: number;
  bidMigrationReminderLeadHours: number;
}

function formatHours(value: number): string {
  return `${String(value)} h`;
}

function formatUpdatedAt(value: Date | string | null): string {
  if (!value) {
    return 'Valeurs par défaut, jamais modifiées.';
  }
  const date = value instanceof Date ? value : new Date(value);
  return `Dernière modification le ${date.toLocaleString('fr-FR')}.`;
}

function DelaysPage(): React.ReactElement {
  const utils = trpc.useUtils();
  const delays = trpc.admin.platformDelays.useQuery();
  const update = trpc.admin.updatePlatformDelays.useMutation({
    onSuccess: () => {
      setDraft(null);
      void utils.admin.platformDelays.invalidate();
    },
  });
  const [draft, setDraft] = React.useState<Draft | null>(null);

  const data = delays.data;
  const current: Draft = draft ?? {
    bidMigrationHours: data?.bidMigrationHours ?? 0,
    bidMigrationReminderLeadHours: data?.bidMigrationReminderLeadHours ?? 0,
  };

  function patch(value: Partial<Draft>): void {
    setDraft({ ...current, ...value });
  }

  const leadTooHigh = current.bidMigrationReminderLeadHours >= current.bidMigrationHours;
  const outOfRange =
    current.bidMigrationHours < 1 ||
    current.bidMigrationHours > MAX_HOURS ||
    current.bidMigrationReminderLeadHours < 0;
  const canSave = !leadTooHigh && !outOfRange && !update.isPending;
  const reminderAt = current.bidMigrationHours - current.bidMigrationReminderLeadHours;

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-6">
      <PageHeader
        title="Délais"
        subtitle="Délais de la plateforme modifiables sans redéploiement."
      />
      {delays.isLoading ? (
        <p className="text-muted-foreground text-sm">Chargement…</p>
      ) : !data ? (
        <p className="text-destructive text-sm">Impossible de charger les délais.</p>
      ) : (
        <div className="bg-card border-border space-y-5 rounded-xl border p-5">
          <div>
            <h2 className="text-[15px] font-semibold">Migration des devis</h2>
            <p className="text-muted-foreground mt-1 max-w-2xl text-sm">
              Quand un Requester publie une modification majeure, les devis déjà déposés passent en
              « à migrer ». Passé le délai ci-dessous, ils expirent et ne sont plus sélectionnables.
            </p>
          </div>

          <div className="flex flex-wrap items-end gap-4">
            <label className="flex flex-col gap-1 text-xs">
              <span className="text-muted-foreground">Délai de migration (heures)</span>
              <input
                type="number"
                min={1}
                max={MAX_HOURS}
                className="border-border bg-background w-28 rounded-lg border px-3 py-1.5 text-sm tabular-nums"
                value={current.bidMigrationHours}
                onChange={(e) => patch({ bidMigrationHours: Number(e.target.value) })}
              />
            </label>
            <label className="flex flex-col gap-1 text-xs">
              <span className="text-muted-foreground">Rappel avant échéance (heures)</span>
              <input
                type="number"
                min={0}
                max={MAX_HOURS - 1}
                className="border-border bg-background w-28 rounded-lg border px-3 py-1.5 text-sm tabular-nums"
                value={current.bidMigrationReminderLeadHours}
                onChange={(e) => patch({ bidMigrationReminderLeadHours: Number(e.target.value) })}
              />
            </label>
            <button
              type="button"
              disabled={!canSave}
              onClick={() =>
                update.mutate({
                  bidMigrationHours: current.bidMigrationHours,
                  bidMigrationReminderLeadHours: current.bidMigrationReminderLeadHours,
                })
              }
              className="bg-primary text-primary-foreground ml-auto rounded-lg px-4 py-1.5 text-sm font-medium disabled:opacity-50"
            >
              {update.isPending ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>

          <div className="border-border bg-muted/40 rounded-lg border p-4">
            <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
              Ce que vivra le prestataire
            </p>
            {leadTooHigh || outOfRange ? (
              <p className="text-destructive mt-2 text-sm">
                Le rappel doit partir avant l’échéance : saisissez un rappel strictement inférieur
                au délai de migration (1 à {String(MAX_HOURS)} heures).
              </p>
            ) : (
              <p className="mt-2 text-sm">
                Rappel envoyé{' '}
                <span className="font-mono tabular-nums">{formatHours(reminderAt)}</span> après la
                nouvelle version, puis expiration du devis à{' '}
                <span className="font-mono tabular-nums">
                  {formatHours(current.bidMigrationHours)}
                </span>
                .
              </p>
            )}
          </div>

          {update.error && <p className="text-destructive text-xs">{update.error.message}</p>}
          <p className="text-muted-foreground text-xs">
            {formatUpdatedAt(data.updatedAt)} Les devis déjà en attente de migration suivent
            immédiatement le nouveau délai.
          </p>
        </div>
      )}
    </div>
  );
}
